import React from 'react';
import { motion } from 'framer-motion';
import { Database, GitBranch, FileSignature, ShieldCheck } from 'lucide-react';

// Evidence lineage for any investigation — connects raw events → correlated facts → packet
const NODES = [
  {
    id: 'raw',
    icon: Database,
    stage: 'Stage 1',
    title: 'Raw sources',
    sub: '127,847 CloudTrail · 1,247 Config · 847 GitHub PRs',
    detail: 'Ingested continuously from 5 live connectors. SHA-256 per event. 14 months retention, S3 Object Lock.',
  },
  {
    id: 'corr',
    icon: GitBranch,
    stage: 'Stage 2',
    title: 'Correlation',
    sub: 'Identity ↔ Image ↔ Trust path ↔ PCI access',
    detail: 'APEX joins actor, asset, change, and data-sensitivity facts into one connected graph.',
  },
  {
    id: 'facts',
    icon: FileSignature,
    stage: 'Stage 3',
    title: 'Evidence facts',
    sub: '6 patterns · 3 critical findings · 4 controls',
    detail: 'Each fact links to immutable source events. No fact exists without provenance.',
  },
  {
    id: 'packet',
    icon: ShieldCheck,
    stage: 'Stage 4',
    title: 'Signed packet',
    sub: 'EVD-2026-042 · ed25519 signed',
    detail: 'Tamper-evident bundle. Anyone with the hash can verify integrity offline.',
  },
];

export default function EvidenceLineage({ inv }) {
  return (
    <div data-testid="evidence-lineage" className="space-y-4">
      <div>
        <h3 className="text-sm font-bold text-apex-ink">Evidence lineage</h3>
        <p className="text-[11px] text-apex-neutral mt-0.5">From raw event to signed packet — every claim in this investigation traces back to immutable evidence.</p>
      </div>

      {/* Flow graph */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5">
        <LineageFlow nodes={NODES} />

        {/* Stats below */}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Stat label="Events analysed" value={(inv?.evidence?.events_analyzed ?? 127847).toLocaleString()} />
          <Stat label="Evidence records" value={inv?.evidence?.evidence_records ?? 28} />
          <Stat label="Controls covered" value={inv?.evidence?.controls_affected ?? 4} />
        </div>

        {/* Hash strip */}
        <div className="mt-4 rounded-md bg-apex-shell text-slate-300 px-3 py-2 text-[11px] font-mono flex items-center justify-between">
          <span>Packet hash</span>
          <span className="text-apex-lens truncate ml-3">{inv?.evidence?.packet_hash?.slice(0, 36) ?? 'sha256:bc3f9a01d2e7c5f4a6b8d9e0f1a2b3c4'}…</span>
        </div>
      </div>
    </div>
  );
}

function LineageFlow({ nodes }) {
  return (
    <div className="relative">
      {/* Desktop: horizontal flow with connectors */}
      <div className="hidden md:grid md:grid-cols-[1fr,auto,1fr,auto,1fr,auto,1fr] items-stretch gap-0">
        {nodes.map((n, i) => (
          <React.Fragment key={n.id}>
            <LineageNode node={n} index={i} />
            {i < nodes.length - 1 && <Connector index={i} />}
          </React.Fragment>
        ))}
      </div>

      {/* Mobile: vertical stack */}
      <div className="md:hidden space-y-3">
        {nodes.map((n, i) => (
          <React.Fragment key={n.id}>
            <LineageNode node={n} index={i} />
            {i < nodes.length - 1 && (
              <div className="flex justify-center">
                <ConnectorVertical />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function LineageNode({ node, index }) {
  const Icon = node.icon;
  const isPacket = node.id === 'packet';
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`lineage-node-${node.id}`}
      className={`relative rounded-lg border p-3 ${
        isPacket
          ? 'bg-apex-shell border-apex-lens text-white shadow-[0_0_0_1px_rgba(13,148,136,0.15)]'
          : 'bg-slate-50/60 border-slate-200 text-apex-ink'
      }`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <span className={`w-7 h-7 rounded-md inline-flex items-center justify-center ${isPacket ? 'bg-apex-lens/20 text-apex-lens' : 'bg-apex-lens/10 text-apex-lens'}`}>
            <Icon className="w-3.5 h-3.5" />
          </span>
          <span className={`text-[10px] uppercase tracking-wider font-semibold ${isPacket ? 'text-slate-400' : 'text-apex-neutral'}`}>{node.stage}</span>
        </div>
        {isPacket && (
          <span className="w-1.5 h-1.5 rounded-full bg-apex-lens shadow-[0_0_8px_rgba(13,148,136,0.6)]" />
        )}
      </div>
      <h4 className={`text-sm font-semibold leading-tight ${isPacket ? 'text-white' : 'text-apex-ink'}`}>{node.title}</h4>
      <p className={`text-[11px] mt-1 font-mono ${isPacket ? 'text-slate-300' : 'text-apex-ink-2'}`}>{node.sub}</p>
      <p className={`text-[11px] mt-2 leading-relaxed ${isPacket ? 'text-slate-400' : 'text-apex-neutral'}`}>{node.detail}</p>
    </motion.div>
  );
}

function Connector({ index }) {
  return (
    <div className="flex items-center justify-center px-1.5" aria-hidden="true">
      <svg viewBox="0 0 60 8" preserveAspectRatio="none" className="w-full h-2">
        <line x1="0" y1="4" x2="60" y2="4" stroke="#0D9488" strokeOpacity="0.2" strokeWidth="2" />
        <motion.line
          x1="0" y1="4" x2="60" y2="4"
          stroke="#0D9488" strokeWidth="2" strokeDasharray="4 4"
          initial={{ strokeDashoffset: 0 }}
          animate={{ strokeDashoffset: -16 }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'linear', delay: index * 0.2 }}
        />
      </svg>
    </div>
  );
}

function ConnectorVertical() {
  return (
    <svg viewBox="0 0 8 36" className="w-2 h-9" aria-hidden="true">
      <line x1="4" y1="0" x2="4" y2="36" stroke="#0D9488" strokeOpacity="0.2" strokeWidth="2" />
      <motion.line
        x1="4" y1="0" x2="4" y2="36"
        stroke="#0D9488" strokeWidth="2" strokeDasharray="4 4"
        initial={{ strokeDashoffset: 0 }}
        animate={{ strokeDashoffset: -16 }}
        transition={{ duration: 1.6, repeat: Infinity, ease: 'linear' }}
      />
    </svg>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <div className="text-[10px] uppercase tracking-wider text-apex-neutral font-semibold">{label}</div>
      <div className="text-lg font-bold tabular text-apex-ink mt-0.5">{value}</div>
    </div>
  );
}
